<template>
  <header>
    <a-form class="form" layout="vertical" ref="formRef" :model="formState" :rules="rules">
      <a-form-item v-if="user?.customer?.type_business == 1" label="Empresa" name="entity" required>
        <entity-picker
          :showAll="false"
          defaultBehavior
          :value="formState.entity"
          @update:value="formState.entity = $event"
        />
      </a-form-item>

      <a-form-item label="Campaña" name="portfolio" required>
        <campaign-picker
          :showAll="false"
          :value="formState.portfolio"
          @update:value="formState.portfolio = $event"
          :entity="formState.entity"
        />
      </a-form-item>

      <a-form-item v-if="user?.customer?.enable_bases" label="Base" name="base" required>
        <bases-picker
          defaultBehavior
          :value="formState.base"
          @update:value="formState.base = $event"
          :entity="formState.entity"
          :portfolio="formState.portfolio"
        />
      </a-form-item>

      <a-form-item label="Cola" required>
        <QueuePicker
          :showAll="false"
          defaultBehavior
          v-model:value="formState.queue"
          :portfolio="formState.portfolio"
        />
      </a-form-item>

      <PlanSeparatedDatePicker
        v-model:startDate="formState.startDate"
        v-model:endDate="formState.endDate"
        startPlaceholder="-- / -- / ----"
        endPlaceholder="-- / -- / ----"
      />
    </a-form>

    <div class="actions">
      <span v-if="totalRecords !== null" class="total">{{ totalRecords }} Registros</span>

      <a-tooltip placement="top">
        <template #title>
          <span v-if="isGlobalDownloading">Tiene una descarga pendiente</span>
          <span v-else>
            Se descargarán únicamente los datos de la tabla de acuerdo a los filtros seleccionados.
          </span>
        </template>

        <a-button
          type="link"
          :disabled="isButtonDisabled || isGlobalDownloading"
          :loading="isDownloading"
          @click="handleDownload"
        >
          Descargar
          <DownloadOutlined />
        </a-button>
      </a-tooltip>

      <a-button type="primary" :disabled="isButtonDisabled || isReportLoading" :loading="isReportLoading" @click="handleSearch">
        Buscar
        <SearchOutlined />
      </a-button>
    </div>
  </header>

  <CustomScrollbar height="calc(100vh - 312px)">
    <a-spin :spinning="isReportLoading">
      <TimeSectionCallsChart :data="reportData" title="Gráfico de tramo horario" />

      <a-table
        class="table"
        rowClassName="table"
        size="middle"
        :columns="timeSectionColumns"
        :data-source="reportData"
        :scroll="{ x: 1500 }"
        :pagination="false"
      >
        <template #nsRespondedCallsTitle>
          Llamadas atendidas del NS

          <a-tooltip title="Llamadas atendidas del nivel de servicio" arrow-point-at-center>
            <InfoCircleOutlined />
          </a-tooltip>
        </template>

        <template #tmoTitle>
          TMO

          <a-tooltip arrow-point-at-center>
            <template #title>
              Tiempo medio operativo. Es la suma de los segundos del tiempo hablado y el tiempo ACW / cantidad de
              llamadas atendidas.
            </template>

            <InfoCircleOutlined />
          </a-tooltip>
        </template>

        <template #tmeTitle>
          TME

          <a-tooltip placement="topRight" arrow-point-at-center>
            <template #title>
              Tiempo medio de espera. Es la suma de los segundos que los contactos pasan esperando
              una respuesta / cantidad de llamadas atendidas.
            </template>

            <InfoCircleOutlined />
          </a-tooltip>
        </template>

        <template #thTitle>
          TH

          <a-tooltip placement="topRight" arrow-point-at-center>
            <template #title>
              Tiempo hablado total. Se calcula desde, fecha y hora que cae la llamada al usuario
              hasta que se corta la llamada (sin contar el ACW.)
            </template>

            <InfoCircleOutlined />
          </a-tooltip>
        </template>

        <template #ttTitle>
          TT

          <a-tooltip placement="topRight" arrow-point-at-center>
            <template #title>
              Tiempo tipificado. Se calcula desde, fecha y hora que cae la llamada al agente hasta
              que guarda una tipificación.
            </template>

            <InfoCircleOutlined />
          </a-tooltip>
        </template>

        <template #percentCell="{ text }">
          {{ String(text * 100).length > 4 ? (text * 100).toFixed(2) : text * 100 }}%
        </template>

        <template #timeCell="{ text }">
          {{ String(text).length > 4 ? text?.toFixed(2) : text ?? 0 }}
        </template>
      </a-table>
    </a-spin>
  </CustomScrollbar>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, SearchOutlined, InfoCircleOutlined } from "@ant-design/icons-vue";

import { fetchTimeSectionType, downloadTimeSectionType } from "@/app/reports/services";
import { timeSectionColumns } from "@/app/reports/utils/tableColumns/customerSupport";
import { formatTimeSectionData } from "@/app/reports/utils/helpers";

import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import BasesPicker from "@/app/shared/components/pickers/BasesPicker.vue";
import QueuePicker from "@/app/shared/components/pickers/QueuePicker.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import TimeSectionCallsChart from "./charts/TimeSectionCallsChart.vue";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";

import PlanSeparatedDatePicker from "@/app/shared/components/pickers/PlanSeparatedDatePicker.vue";

const store = useStore();

const user = computed(() => store.getters["getDataUser"]);

// Form
const formState = reactive({
  portfolio: null,
  queue: null,
  startDate: null,
  endDate: null,
});
const isButtonDisabled = computed(() => {
  for (const key in formState) {
    if (!formState[key]) return true;
  }

  return false;
});
const isDownloading = ref(false);
const { isGlobalDownloading } = useGlobalDownloadState();

// Table
const reportData = ref(null);
const isReportLoading = ref(false);

const fetchReportData = async () => {
  isReportLoading.value = true;
  const response = await fetchTimeSectionType({ filters: formState });
  reportData.value = formatTimeSectionData({ rawData: response?.data });
  isReportLoading.value = false;
};

const handleSearch = async () => {
  await fetchReportData();
};

const handleDownload = async () => {
  isDownloading.value = true;
  await downloadTimeSectionType({ filters: formState });
  isDownloading.value = false;
};
</script>

<style lang="sass" scoped>
header
  display: flex
  justify-content: space-between
  align-items: flex-end
  margin: 15px 0

.form
  display: flex
  gap: 10px

.ant-form-item
  margin: 0
  width: 180px

.table
  margin-top: 15px

:deep(.ant-form-item-label)
  padding: 0

:deep(.table.ant-table-row:last-child)
  background-color: $blue-1

:deep(.table.ant-table-row:last-child .ant-table-cell-fix-left)
  background-color: $blue-1 !important

.actions
  display: flex
  align-items: center
  gap: 10px
</style>
